/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { Collapsible } from 'src/core/components/Collapsible';
import { DragAndDropInput } from 'src/core/components/DragAndDropInput';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

type CluUploadUtterancesModalDto = { files: File[] };

const messages = defineMessages({
    modalTitle: {
        id: 'clu.cluUploadUtterancesModal.modalTitle',
        defaultMessage: 'Upload a labeled utterance file'
    },
    filePickerLabel: {
        id: 'clu.cluUploadUtterancesModal.filePickerLabel',
        defaultMessage: 'Choose sublist file (JSON format) ...'
    },
    sampleLink: {
        id: 'clu.cluUploadUtterancesModal.sampleLink',
        defaultMessage: 'Learn more about the supported file format'
    },
    sampleTitle: {
        id: 'clu.cluUploadUtterancesModal.sampleTitle',
        defaultMessage: 'Sample utterances syntax'
    },
    sampleBody: {
        id: 'clu.cluUploadUtterancesModal.sampleBody',
        defaultMessage: 'The file should contain an array of utterances that follow the following JSON format:'
    }
});

const sampleConversationJSON = [
    {
        intent: 'Quote',
        language: 'en-us',
        text: 'I left my heart in Alexandria Egypt',
        entities: [{ category: 'CityName', offset: 19, length: 10 }]
    },
    {
        language: 'en-us',
        intent: 'BookFlight',
        text: 'Book me a flight to Alexandria Egypt',
        entities: [
            { category: 'CityName', offset: 20, length: 10 },
            { category: 'CountryName', offset: 31, length: 5 }
        ]
    }
];
const sampleOrchestrationJSON = [
    { intent: 'Quote', language: 'en-us', text: 'I left my heart in Alexandria Egypt' },
    { language: 'en-us', intent: 'BookFlight', text: 'Book me a flight to Alexandria Egypt' }
];

export const CluUploadUtterancesModal = withModalMounter(
    (props: BasicModalProps<CluUploadUtterancesModalDto> & { projectType: CluProjectType }) => {
        const { onSubmit, onDismiss, projectType } = props;
        const { localize } = useLocalization();
        const [collapse, setCollapse] = React.useState(true);
        const [files, setFiles] = React.useState<File[]>([]);

        return (
            <BasicModal
                {...props}
                onDismiss={onDismiss}
                valid={files.length > 0}
                onSubmit={() => onSubmit({ files })}
                dialogContentProps={{ title: localize(messages.modalTitle) }}>
                <Stack tokens={{ childrenGap: 16 }}>
                    <DragAndDropInput showUploadedFiles extensionsToAccept=".json" isMultiple onDropFiles={setFiles} />

                    <Collapsible title={localize(messages.sampleLink)} collapsed={collapse} onTitleClick={() => setCollapse(!collapse)}>
                        <LuisText variant="large" block as="h3" styles={{ root: { marginBottom: 10 } }}>
                            {localize(messages.sampleTitle)}
                        </LuisText>

                        <LuisText>{localize(messages.sampleBody)}</LuisText>

                        <code>
                            <pre>
                                {JSON.stringify(projectType === 'Conversation' ? sampleConversationJSON : sampleOrchestrationJSON, null, 4)}
                            </pre>
                        </code>
                    </Collapsible>
                </Stack>
            </BasicModal>
        );
    }
);
